import { useState } from 'react';

import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import { Box, Button, DialogActions, DialogContent, Divider, TextField, Typography } from '@mui/material';
import { useAppDispatch } from 'app/hooks';
import { PopOver } from 'common/components/PopOver';
import { StepFunctionExecutionSignalType } from 'common/constants/admin';
import { Roles } from 'common/constants/roles';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { postStepFunctionExecutionTaskSignal } from 'common/services/api/StepFunctions/StepFunctions.thunks';
import { useSnackbar } from 'notistack';

import { removeRow } from '../accountLinkingDashboardSlice';
import { StepFunctionExecution, StepFunctionExecutionDetails } from '../AccountLinkingDashboardTypeDefs';

type AccountLinkingDashboardDialogProps = {
    row: StepFunctionExecution
    rowDetails: StepFunctionExecutionDetails
    open: boolean,
    onClose: () => void,
}

export function AccountLinkingDashboardIgnoreDialog({
    row,
    rowDetails,
    open,
    onClose,
}: Readonly<AccountLinkingDashboardDialogProps>) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const disabled = usePalmButtonEvaluator([ Roles.Admin, Roles.MultiTenantRO, Roles.UserManager ]);
    const [ignoreReason, setIgnoreReason] = useState('');
    const [isError, setIsError] = useState(false);

    const handleClose = () => {
        setIgnoreReason('');
        setIsError(false);
        onClose();
    };

    const handleIgnoreClick = () => {
        if (!ignoreReason || ignoreReason.length == 0) {
            setIsError(true);
            return;
        }

        if (rowDetails.executionId) {
            const requestParams = {
                signalType: StepFunctionExecutionSignalType.IGNORE,
                executionType: row.executionType,
                userReason: ignoreReason,
                executionId: rowDetails?.executionId
            };
            dispatch(postStepFunctionExecutionTaskSignal(requestParams))
                .unwrap()
                .then(() => {
                    enqueueSnackbar(`Clearing step function execution with ID: ${rowDetails.executionId?.split(':')[1]}`, { variant: 'success' });
                    dispatch(removeRow(row.executionId));
                })
                .catch(() => {
                    enqueueSnackbar(`There was an error while clearing step function with execution ID: ${rowDetails.executionId?.split(':')[1]}`,
                        { variant: 'error' });
                });
        }
        handleClose();
    };

    const handleInput = (input: string) => {
        if (isError) {
            setIsError(false);
        }
        setIgnoreReason(input);
    };

    return (
        <PopOver
            title={'Ignore Execution'}
            open={open}
            onClose={handleClose}
        >
            <DialogContent
                sx={{
                    textAlign: 'left',
                    marginTop: '1.5rem',
                    padding: '1.5rem 2rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
                dividers>
                <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Execution Id: <Typography component="span" color="text.primary" fontWeight="400">{rowDetails.executionId?.split(':')[1]}</Typography>
                </Typography>
                <Typography
                    textAlign='center'
                    color="info.light"
                    sx={{ paddingTop: '0.7rem', maxWidth: '20rem' }}>
                    Please provide a reason
                </Typography>
                <TextField
                    multiline
                    minRows={2}
                    error={isError}
                    id={isError ? 'outlined-error-helper-text' : ''}
                    helperText={isError ? 'Missing reason' : ''}
                    onChange={e => handleInput(e.target.value)}
                    sx={{ width: '35ch' }}
                    inputProps={{ 'data-testid': 'reason-field' }}
                />
            </DialogContent>
            <Box sx={{
                backgroundColor: 'error.main',
                height: '3rem',
                width: '3rem',
                borderRadius: '100%',
                position: 'absolute',
                left: '50%',
                transform: 'translate(-50%, -50%)',
            }}>
                <CancelPresentationIcon fontSize='large'
                    sx={{
                        color: 'common.white',
                        marginTop: '7px',
                        marginLeft: '6px'
                    }}
                />
            </Box>
            <Divider sx={{ borderColor: 'transparent' }}/>
            <DialogActions sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                <Button variant='contained'
                    disabled={disabled}
                    onClick={handleIgnoreClick}
                    data-testid='ignore-execution'
                    color='error'>
                        Ignore
                </Button>
                <Button variant="outlined" onClick={handleClose}>
                    Cancel
                </Button>
            </DialogActions>
        </PopOver>
    );
}
